import React from 'react';
import blocksToHtml from '@sanity/block-content-to-html';
import { Schema } from '@sanity/schema';
import { htmlToBlocks } from '@sanity/block-tools';
import { objects } from '@breezeapp/breeze-schema';
import { TPortableText } from '@typings/TPortableText';
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { PortableTextInputToolbar } from './PortableTextInputToolbar';
import { twMerge } from '@utils/tailwind';

const richtTextSchema = {
  name: 'richText',
  type: 'object',
  fields: [{
    name: 'content',
    type: 'array',
    of: [{ type: 'block' }, { type: objects.inlineImage.object.name }],
  }]
}

const defaultSchema = Schema.compile({ types: [richtTextSchema, objects.inlineImage.object] })
  .get(richtTextSchema.name)
  .fields.find((field: { name: string }) => field.name === 'content').type;

type SanitySchemaField = {
  jsonType: string;
  name: string;
  title: string;
  type: SanitySchemaField | null;
  of: SanitySchemaField[];
}

export type ControlRef = {
  setHtml: React.Dispatch<React.SetStateAction<string>>;
  setPortableText: React.Dispatch<React.SetStateAction<TPortableText>>;
}

type Props = {
  className?: string;
  style?: React.CSSProperties;
  schema?: SanitySchemaField;
  disabled?: boolean;
  value?: TPortableText;
  htmlState?: [string, React.Dispatch<React.SetStateAction<string>>];
  controlRef?: React.MutableRefObject<ControlRef | null>;
  onChange?: (value: TPortableText) => void;
}

export const defaultHtmlSerializers = {
  types: {
    // @TODO typing
    inlineImage: (props: { node?: any; }) => {
      return blocksToHtml.h('img', { src: props.node?.src ?? '' })
    }
  }
};

export const PortableTextInput = React.forwardRef<HTMLDivElement, Props>(({
  value,
  disabled,
  schema,
  htmlState,
  controlRef,
  className = '',
  onChange,
}, ref) => {
  const [html, setHtml] = htmlState ?? React.useState(
    value
      ? blocksToHtml({
        blocks: value,
        serializers: defaultHtmlSerializers,
      })
      : ''
  );

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit,
      Image.configure({
        HTMLAttributes: {
          style: 'max-width: 100%; height: auto',
        },
      }),
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: html,
    onUpdate: ({ editor }) => {
      if (!onChange) return;

      const html = editor.getHTML();
      setHtml(html);

      // Convert HTML → Sanity blocks, fixing type mismatch
      const blocks = htmlToBlocks(html, schema ?? defaultSchema, {
        rules: [
          {
            deserialize: (el: any, next: any, createBlock: any) => {
              if (el.tagName && el.tagName.toLowerCase() === 'img') {
                return createBlock({
                  _type: 'inlineImage',
                  src: el.getAttribute('src'),
                });
              }
              return undefined;
            },
          },
        ],
      }) as unknown as TPortableText;

      onChange(blocks);
    },
  });

  React.useEffect(() => {
    if (controlRef) {
      controlRef.current = {
        setHtml,
        setPortableText: (t) => {
          setHtml(
            blocksToHtml({
              blocks: t,
              serializers: defaultHtmlSerializers,
            })
          );
        },
      };
    }
  }, [setHtml]);

  return (
    <div
      className={twMerge(
        'border border-gray-300 rounded-md transition-all focus-within:shadow-md focus-within:shadow-gray-600/20 [&_.ProseMirror]:outline-none',
        disabled && 'opacity-40 pointer-events-none',
        className
      )}
    >
      <div className="p-2 border-b border-gray-300 flex">
        <PortableTextInputToolbar editor={editor} />
      </div>
      <div className="p-4">
        <EditorContent editor={editor} className="prose-tiptap" />
      </div>
    </div>
  );
});
